import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Skeleton,
  Alert,
  Card,
  CardActionArea,
} from '@mui/material';
import { CalendarTodayOutlined, RoomOutlined, PlaceOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AppButton } from '@/shared/components';
import { formatPriceWithAbbr } from '@/shared/utils/formatters';
import { PLACEHOLDER_IMAGES } from '@/shared/constants/placeholders';
import publicationService from '../../my-publications/services/publicationService';
import { OffersMapSummary, UserPublication } from '../../my-publications/types/publication.types';

interface SectionHeaderProps {
  onViewAll: () => void;
}

function SectionHeader({ onViewAll }: Readonly<SectionHeaderProps>) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
      }}
    >
      <Typography variant="h6" fontWeight={700} data-testid="latest-published-offers-title">
        {t('home.latestOffers.title')}
      </Typography>
      <AppButton variant="text" size="small" onClick={onViewAll}>
        {t('home.latestOffers.openSearch')}
      </AppButton>
    </Box>
  );
}

interface LatestOfferCardProps {
  offer: UserPublication;
  testId?: string;
}

function LatestOfferCard({ offer, testId }: Readonly<LatestOfferCardProps>) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const firstProduct = offer.products[0];
  const firstPhoto = firstProduct?.photos[0];
  const unit = firstProduct?.unit;

  const formatDate = (dateString: string): string =>
    new Date(dateString).toLocaleDateString(i18n.language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

  const getUnitLabel = (u: { labelFr: string; labelEn: string }): string => {
    return i18n.language === 'fr' ? u.labelFr : u.labelEn;
  };

  const totalPrice = offer.products.reduce((sum, product) => {
    return sum + (product.unitPrice ?? 0) * (product.quantity ?? 0);
  }, 0);

  return (
    <Card
      sx={{
        borderRadius: 2,
        boxShadow: 1,
        width: '100%',
      }}
      data-testid={testId ?? `latest-offer-card-${offer.id}`}
    >
      <CardActionArea
        onClick={() => navigate(`/offers/${offer.id}`, { state: { from: 'home-latest-offers' } })}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              width: '100%',
              height: 180,
              overflow: 'hidden',
              bgcolor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              src={firstPhoto?.thumbnailUrl || firstPhoto?.url || PLACEHOLDER_IMAGES.product}
              alt={firstProduct?.name ?? offer.title}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                e.currentTarget.src = PLACEHOLDER_IMAGES.product;
              }}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </Box>

          <Box sx={{ p: 2, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {offer.title}
            </Typography>

            {(firstProduct?.material?.subcategory ?? firstProduct?.name) && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {firstProduct?.material?.subcategory ?? firstProduct?.name}
              </Typography>
            )}

            {offer.address && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <RoomOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" noWrap>
                  {offer.address}
                </Typography>
              </Box>
            )}

            {firstProduct && unit && (
              <Typography variant="body2" color="text.secondary">
                {t('home.publications.quantity', {
                  value: firstProduct.quantity,
                  unit: getUnitLabel(unit),
                })}
              </Typography>
            )}

            {offer.startDate && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarTodayOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {t('home.publications.from', { date: formatDate(offer.startDate) })}
                </Typography>
              </Box>
            )}

            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{
                mt: 'auto',
                pt: 0.5,
                alignSelf: 'flex-end',
                textAlign: 'right',
                color: 'primary.main',
              }}
            >
              {t('home.latestOffers.totalPrice', { price: formatPriceWithAbbr(totalPrice) })}
            </Typography>
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
}

export function LatestPublishedOffers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [offers, setOffers] = useState<UserPublication[]>([]);
  const [mapSummary, setMapSummary] = useState<OffersMapSummary>({ offersCount: 0, citiesCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  const extractCity = (address: string): string => {
    const commaIdx = address.lastIndexOf(',');
    if (commaIdx >= 0) {
      return address
        .slice(commaIdx + 1)
        .trim()
        .replace(/^\d+\s*/, '')
        .trim();
    }
    return address.split(' ').slice(0, 2).join(' ');
  };

  useEffect(() => {
    const loadOffers = async () => {
      try {
        setLoading(true);
        const [recentOffers, summaryResult] = await Promise.allSettled([
          publicationService.getRecentPublications(),
          publicationService.getMapSummary(),
        ]);

        if (recentOffers.status === 'rejected') {
          throw recentOffers.reason;
        }

        const latestOffers = recentOffers.value.slice(0, 5);
        setOffers(latestOffers);

        if (summaryResult.status === 'fulfilled') {
          setMapSummary(summaryResult.value);
        } else {
          const fallbackCities = new Set(
            latestOffers.map((offer) => extractCity(offer.address)).filter(Boolean)
          ).size;
          setMapSummary({ offersCount: latestOffers.length, citiesCount: fallbackCities });
          console.warn('Failed to load map summary, using fallback values');
        }

        setError(null);
      } catch (err) {
        console.error('Failed to load latest offers:', err);
        setError(t('home.latestOffers.errorLoading'));
      } finally {
        setLoading(false);
      }
    };

    void loadOffers();
  }, [t]);

  if (loading) {
    return (
      <Box data-testid="latest-published-offers-loading">
        <SectionHeader onViewAll={() => navigate('/search?fromLatestOffers=1')} />
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: 'background.paper',
            boxShadow: 1,
          }}
        >
          <Skeleton variant="rectangular" width={90} height={90} sx={{ borderRadius: 2 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="70%" height={24} />
            <Skeleton variant="text" width="50%" height={20} />
            <Skeleton variant="text" width="60%" height={20} />
          </Box>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <SectionHeader onViewAll={() => navigate('/search?fromLatestOffers=1')} />
        <Alert severity="error" data-testid="latest-published-offers-error">
          {error}
        </Alert>
      </Box>
    );
  }

  if (offers.length === 0) {
    return null;
  }

  return (
    <Box data-testid="home-latest-published-offers-section">
      <SectionHeader onViewAll={() => navigate('/search?fromLatestOffers=1')} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box
          ref={carouselRef}
          sx={{
            display: 'flex',
            gap: 1.5,
            overflowX: 'auto',
            pb: 1,
            px: 0,
            scrollSnapType: 'x mandatory',
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.25)',
              borderRadius: 8,
            },
          }}
        >
          {offers.map(offer => (
            <Box
              key={offer.id}
              sx={{
                scrollSnapAlign: 'start',
                flex: { xs: '0 0 62%', sm: '0 0 48%', md: '0 0 42%' },
                minWidth: 230,
              }}
            >
              <LatestOfferCard
                offer={offer}
                testId={`latest-published-offer-card-${offer.id}`}
              />
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            p: 2,
            borderRadius: 3,
            background: 'linear-gradient(145deg, rgba(27,58,92,0.06), rgba(27,58,92,0.01))',
            border: '1px solid',
            borderColor: 'rgba(27,58,92,0.12)',
            boxShadow: '0 8px 20px rgba(27,58,92,0.08)',
          }}
        >
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.5, color: 'primary.main' }}>
            {t('home.latestOffers.mapBlockTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('home.latestOffers.mapBlockSubtitle')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'stretch', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.9,
                minWidth: 0,
                px: 1.2,
                py: 0.8,
                borderRadius: 2,
                bgcolor: 'rgba(27,58,92,0.08)',
              }}
            >
              <PlaceOutlined sx={{ color: 'primary.main', fontSize: 19 }} />
              <Typography variant="body1" fontWeight={700}>
                {t('home.latestOffers.mapBlockStats', {
                  offers: mapSummary.offersCount,
                  cities: mapSummary.citiesCount,
                })}
              </Typography>
            </Box>
            <AppButton
              variant="outlined"
              size="small"
              onClick={() => navigate('/offers-map')}
            >
              {t('home.latestOffers.viewMap')}
            </AppButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
